import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, X, Building2, MapPin, Calendar, Hash, Download, Filter, SortAsc, SortDesc, Home } from 'lucide-react';

const MahaReraDashboard = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('ALL');
  const [filterDistrict, setFilterDistrict] = useState('ALL');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 100;

  useEffect(() => {
    loadCSV();
  }, []);

  const loadCSV = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/data/maharashtra_rera.csv');
      
      if (!response.ok) {
        throw new Error('Failed to load CSV file');
      }
      
      const text = await response.text();
      parseCSV(text);
    } catch (err) {
      console.error('Error loading CSV:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Robust CSV parser that handles both comma and tab delimited files
  const parseCSV = useCallback((csvText) => {
    try {
      // Split into lines
      const lines = csvText.split(/\r?\n/).filter(line => line.trim());
      
      if (lines.length === 0) {
        throw new Error('CSV file is empty');
      }

      // Detect delimiter - check first line for tabs or commas
      const firstLine = lines[0];
      const delimiter = firstLine.includes('\t') ? '\t' : ',';
      
      console.log('Detected delimiter:', delimiter === '\t' ? 'TAB' : 'COMMA');

      // Parse CSV line with proper quote handling
      const parseLine = (line, delim) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const nextChar = line[i + 1];
          
          if (char === '"' && inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
          } else if (char === '"') {
            // Toggle quote mode
            inQuotes = !inQuotes;
          } else if (char === delim && !inQuotes) {
            // Field separator
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        
        // Add last field
        result.push(current.trim());
        
        return result;
      };

      // Get headers from first line
      const headers = parseLine(lines[0], delimiter).map(h => 
        h.replace(/^["']|["']$/g, '').trim()
      );
      
      console.log('CSV Headers:', headers);
      console.log('Total lines:', lines.length);
      
      const parsedData = [];
      
      // Parse each data line
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines
        if (!line) continue;
        
        try {
          const values = parseLine(line, delimiter).map(v => 
            v.replace(/^["']|["']$/g, '').trim()
          );
          
          // Create object from headers and values
          if (values.length > 0 && values[0]) { // Must have at least first column
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            
            // Validate row has required fields
            if (row.RERA_No || row.Project_Name) {
              parsedData.push(row);
            }
          }
        } catch (lineErr) {
          console.warn(`Error parsing line ${i + 1}:`, lineErr.message);
          // Continue with next line
        }
      }
      
      console.log(`Successfully parsed ${parsedData.length} records`);
      console.log('Sample record:', parsedData[0]);
      
      if (parsedData.length === 0) {
        throw new Error('No valid data found in CSV. Please check the file format.');
      }
      
      setData(parsedData);
      setCurrentPage(1);
      setLoading(false);
    } catch (err) {
      console.error('Error parsing CSV:', err);
      setError('Error parsing CSV: ' + err.message);
      setLoading(false);
    }
  }, []);

  const uniqueStates = useMemo(() => {
    const states = new Set();
    data.forEach(item => {
      if (item.State) states.add(item.State);
    });
    return ['ALL', ...Array.from(states).sort()];
  }, [data]);

  const uniqueDistricts = useMemo(() => {
    const districts = new Set();
    data.forEach(item => {
      if (item.District) districts.add(item.District);
    });
    return ['ALL', ...Array.from(districts).sort()];
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data.length) return [];
    
    return data.filter(item => {
      if (filterState !== 'ALL' && item.State !== filterState) return false;
      if (filterDistrict !== 'ALL' && item.District !== filterDistrict) return false;
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const searchableFields = [
          item.RERA_No,
          item.Project_Name,
          item.Promoter,
          item.District,
          item.State,
          item.Pincode
        ];
        
        return searchableFields.some(field => 
          field && field.toString().toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
  }, [data, searchTerm, filterState, filterDistrict]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !filteredData.length) return filteredData;

    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      const aValue = (a[sortConfig.key] || '').toString().toLowerCase();
      const bValue = (b[sortConfig.key] || '').toString().toLowerCase();
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const exportToCSV = useCallback(() => {
    if (sortedData.length === 0) return;
    
    const headers = Object.keys(sortedData[0]);
    const csvContent = [
      headers.join('\t'), // Use tab delimiter for export
      ...sortedData.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          return value.toString().replace(/\t/g, ' '); // Replace tabs with spaces
        }).join('\t')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maharashtra_rera_filtered_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [sortedData]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setFilterState('ALL');
    setFilterDistrict('ALL');
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);
  }, []);

  if (error) {
    return (
      <div style={{
        fontFamily: '"Montserrat", sans-serif',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '40px'
          }}>
            ⚠️
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#dc2626',
            marginBottom: '12px'
          }}>
            Error Loading Data
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            {error}
          </p>
          <div style={{
            background: '#fef3c7',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '24px',
            textAlign: 'left'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#92400e',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              Troubleshooting steps:
            </p>
            <ul style={{
              fontSize: '13px',
              color: '#92400e',
              paddingLeft: '20px',
              margin: 0
            }}>
              <li>Check if the file exists at: /data/maharashtra_rera.csv</li>
              <li>Verify the CSV file format (should be tab or comma delimited)</li>
              <li>Ensure the file has proper headers in the first row</li>
              <li>Check browser console for detailed error messages</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'inherit'
            }}
          >
            <Home size={16} />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: '"Montserrat", sans-serif',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '20px',
          padding: '40px',
          marginBottom: '24px',
          boxShadow: '0 10px 40px rgba(16, 185, 129, 0.2)'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '900',
            color: 'white',
            marginBottom: '8px',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}>
            Maharashtra RERA Properties
          </h1>
          <p style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            margin: 0
          }}>
            Comprehensive real estate project database
          </p>
        </div>

        {loading ? (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '60px 40px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #f3f4f6',
              borderTopColor: '#10b981',
              borderRadius: '50%',
              margin: '0 auto 24px',
              animation: 'spin 1s linear infinite'
            }} />
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '8px'
            }}>
              Loading Data...
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              Please wait while we load the property records
            </p>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                borderLeft: '4px solid #10b981'
              }}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                  Total Properties
                </div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#111827' }}>
                  {data.length.toLocaleString()}
                </div>
              </div>
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                borderLeft: '4px solid #3b82f6'
              }}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                  Filtered Results
                </div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#111827' }}>
                  {sortedData.length.toLocaleString()}
                </div>
              </div>
              <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                borderLeft: '4px solid #f59e0b'
              }}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                  Districts
                </div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#111827' }}>
                  {uniqueDistricts.length - 1}
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                {/* Search */}
                <div style={{ position: 'relative' }}>
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search properties..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 40px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      transition: 'border 0.2s'
                    }}
                  />
                  {searchTerm && (
                    <X
                      size={18}
                      onClick={() => setSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        cursor: 'pointer'
                      }}
                    />
                  )}
                </div>

                {/* State Filter */}
                <div style={{ position: 'relative' }}>
                  <Filter
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af',
                      pointerEvents: 'none'
                    }}
                  />
                  <select
                    value={filterState}
                    onChange={(e) => setFilterState(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 40px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {uniqueStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                {/* District Filter */}
                <div style={{ position: 'relative' }}>
                  <MapPin
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af',
                      pointerEvents: 'none'
                    }}
                  />
                  <select
                    value={filterDistrict}
                    onChange={(e) => setFilterDistrict(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 12px 12px 40px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {uniqueDistricts.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={clearFilters}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#f3f4f6',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <X size={16} />
                    Clear
                  </button>
                  <button
                    onClick={exportToCSV}
                    disabled={sortedData.length === 0}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: sortedData.length === 0 ? '#f3f4f6' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: sortedData.length === 0 ? '#9ca3af' : 'white',
                      cursor: sortedData.length === 0 ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Download size={16} />
                    Export
                  </button>
                </div>
              </div>
            </div>

            {/* Data Table */}
            {paginatedData.length > 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{
                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                        borderBottom: '2px solid #e5e7eb'
                      }}>
                        {Object.keys(paginatedData[0] || {}).map((key) => (
                          <th
                            key={key}
                            onClick={() => handleSort(key)}
                            style={{
                              padding: '16px 20px',
                              textAlign: 'left',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#374151',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              cursor: 'pointer',
                              position: 'relative',
                              transition: 'background 0.2s',
                              userSelect: 'none',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              {key.replace(/_/g, ' ')}
                              {sortConfig.key === key && (
                                sortConfig.direction === 'asc' ? 
                                  <SortAsc size={14} /> : 
                                  <SortDesc size={14} />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((row, index) => (
                        <tr
                          key={index}
                          style={{
                            borderBottom: '1px solid #f3f4f6',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          {Object.entries(row).map(([key, value], i) => (
                            <td
                              key={i}
                              style={{
                                padding: '16px 20px',
                                fontSize: '14px',
                                color: '#374151'
                              }}
                            >
                              {key === 'RERA_No' ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Hash size={14} color="#10b981" />
                                  <span style={{ fontWeight: '600', color: '#10b981' }}>{value}</span>
                                </div>
                              ) : key === 'Project_Name' ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Building2 size={14} color="#6b7280" />
                                  <span style={{ fontWeight: '600' }}>{value}</span>
                                </div>
                              ) : key === 'District' ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <MapPin size={14} color="#3b82f6" />
                                  {value}
                                </div>
                              ) : key === 'Last_Modified' ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Calendar size={14} color="#f59e0b" />
                                  {value}
                                </div>
                              ) : key === 'Details_URL' || key === 'Original_Application_URL' ? (
                                <a 
                                  href={value} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#10b981',
                                    textDecoration: 'none',
                                    fontWeight: '500'
                                  }}
                                >
                                  View
                                </a>
                              ) : value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{
                    padding: '20px',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData.length)} of {sortedData.length.toLocaleString()} results
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        style={{
                          padding: '8px 12px',
                          background: currentPage === 1 ? '#f3f4f6' : 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: currentPage === 1 ? '#9ca3af' : '#374151',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      >
                        First
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: '8px 16px',
                          background: currentPage === 1 ? '#f3f4f6' : 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: currentPage === 1 ? '#9ca3af' : '#374151',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      >
                        Previous
                      </button>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        Page {currentPage} of {totalPages.toLocaleString()}
                      </div>

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '8px 16px',
                          background: currentPage === totalPages ? '#f3f4f6' : 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: currentPage === totalPages ? '#9ca3af' : '#374151',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '8px 12px',
                          background: currentPage === totalPages ? '#f3f4f6' : 'white',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: currentPage === totalPages ? '#9ca3af' : '#374151',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      >
                        Last
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '60px 40px',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '8px'
                }}>
                  No Results Found
                </h3>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>
                  Try adjusting your filters
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        input:focus, select:focus {
          border-color: #10b981 !important;
        }
        
        button:hover:not(:disabled) {
          opacity: 0.9;
        }
      `}</style>
    </div>
  );
};

export default MahaReraDashboard;
